import {
  PickupPointNetwork,
  ShippingDeliveryMethod,
  ShippingFreeShippingRuleInput,
} from '@/lib/graphql/generated/graphql';
import {
  ShippingPolicy,
  ShippingPolicyState,
  daysToOfferDuration,
  offerDurationToDays,
} from '@/lib/types/shipping-policy';

const findRule = (
  rules: ShippingFreeShippingRuleInput[],
  deliveryMethod: ShippingDeliveryMethod
) => rules.find((rule) => rule.deliveryMethod === deliveryMethod);

export const enforceFreePickupPointPolicy = (
  policy: ShippingPolicy
): ShippingPolicy =>
  policy.pickupPointEnabled && policy.pickupPointDeliveryPrice === 0
    ? {
        ...policy,
        minimumOrderSplitByDeliveryMode: true,
        pickupPointMinimumOrderAmount: null,
      }
    : policy;

export const toShippingPolicyView = (
  state: ShippingPolicyState
): ShippingPolicy => {
  const homeMinimum = findRule(
    state.freeShippingRules,
    ShippingDeliveryMethod.HomeDelivery
  )?.freeShippingMinimumOrderAmount;
  const pickupPointMinimum = findRule(
    state.freeShippingRules,
    ShippingDeliveryMethod.PickupPoint
  )?.freeShippingMinimumOrderAmount;
  const hasHomeMinimum = homeMinimum !== undefined;
  const hasPickupPointMinimum = pickupPointMinimum !== undefined;
  const pickupPointEnabled = state.pickupPointNetwork !== null;
  const pickupPointDeliveryPrice =
    state.basicFrPickupPointDeliveryPrice === null
      ? null
      : state.basicFrPickupPointDeliveryPrice / 100;
  const isPickupPointDeliveryFree =
    pickupPointEnabled && pickupPointDeliveryPrice === 0;
  const minimumOrderSplitByDeliveryMode =
    pickupPointEnabled &&
    (isPickupPointDeliveryFree ||
      hasHomeMinimum !== hasPickupPointMinimum ||
      (hasHomeMinimum &&
        hasPickupPointMinimum &&
        homeMinimum !== pickupPointMinimum));

  return {
    minimumOrderEnabled:
      hasHomeMinimum || (!isPickupPointDeliveryFree && hasPickupPointMinimum),
    minimumOrderSplitByDeliveryMode,
    minimumOrderAmount: hasHomeMinimum ? homeMinimum / 100 : null,
    pickupPointMinimumOrderAmount:
      !isPickupPointDeliveryFree &&
      minimumOrderSplitByDeliveryMode &&
      hasPickupPointMinimum
        ? pickupPointMinimum / 100
        : null,
    timeLimitedEnabled: state.deliveryOfferedNumberOfDays !== null,
    offerDuration: daysToOfferDuration(state.deliveryOfferedNumberOfDays),
    pickupPointEnabled,
    pickupPointNetwork: state.pickupPointNetwork,
    pickupPointDeliveryPrice,
    allShippingFeesZero: state.allShippingFeesZero,
  };
};

const composeFreeShippingRules = (
  policy: ShippingPolicy
): ShippingFreeShippingRuleInput[] => {
  if (!policy.minimumOrderEnabled) return [];

  const isSplitByDeliveryMode =
    policy.pickupPointEnabled && policy.minimumOrderSplitByDeliveryMode;
  const pickupPointAmount = isSplitByDeliveryMode
    ? policy.pickupPointMinimumOrderAmount
    : policy.minimumOrderAmount;
  const rules: ShippingFreeShippingRuleInput[] = [];

  if (policy.minimumOrderAmount !== null) {
    rules.push({
      countries: null,
      deliveryMethod: ShippingDeliveryMethod.HomeDelivery,
      freeShippingMinimumOrderAmount: Math.round(
        policy.minimumOrderAmount * 100
      ),
      commercialOfferMinimumOrderAmount: null,
    });
  }

  if (policy.pickupPointEnabled && pickupPointAmount !== null) {
    rules.push({
      countries: null,
      deliveryMethod: ShippingDeliveryMethod.PickupPoint,
      freeShippingMinimumOrderAmount: Math.round(pickupPointAmount * 100),
      commercialOfferMinimumOrderAmount: null,
    });
  }

  return rules;
};

export const toShippingPolicyState = (
  policy: ShippingPolicy
): ShippingPolicyState => {
  const normalizedPolicy = enforceFreePickupPointPolicy(policy);

  return {
    freeShippingRules: composeFreeShippingRules(normalizedPolicy),
    deliveryOfferedNumberOfDays: normalizedPolicy.timeLimitedEnabled
      ? offerDurationToDays(normalizedPolicy.offerDuration)
      : null,
    pickupPointNetwork: normalizedPolicy.pickupPointEnabled
      ? normalizedPolicy.pickupPointNetwork
      : null,
    basicFrPickupPointDeliveryPrice:
      normalizedPolicy.pickupPointEnabled &&
      normalizedPolicy.pickupPointDeliveryPrice !== null
        ? Math.round(normalizedPolicy.pickupPointDeliveryPrice * 100)
        : null,
    allShippingFeesZero: normalizedPolicy.allShippingFeesZero,
  };
};

export const withPickupPointEnabled = (
  policy: ShippingPolicy,
  enabled: boolean
): ShippingPolicy =>
  enforceFreePickupPointPolicy({
    ...policy,
    pickupPointEnabled: enabled,
    pickupPointNetwork: enabled
      ? (policy.pickupPointNetwork ?? PickupPointNetwork.MondialRelay)
      : null,
    pickupPointMinimumOrderAmount: enabled
      ? policy.pickupPointMinimumOrderAmount
      : null,
    minimumOrderSplitByDeliveryMode: enabled
      ? policy.minimumOrderSplitByDeliveryMode
      : false,
    minimumOrderEnabled: enabled
      ? policy.minimumOrderEnabled
      : policy.minimumOrderEnabled && policy.minimumOrderAmount !== null,
  });

export const withoutFreeShippingConditions = (
  policy: ShippingPolicy
): ShippingPolicy => ({
  ...policy,
  minimumOrderEnabled: false,
  minimumOrderSplitByDeliveryMode: false,
  minimumOrderAmount: null,
  pickupPointMinimumOrderAmount: null,
  timeLimitedEnabled: false,
  offerDuration: null,
});
